"use client"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import { useToast } from "@/hooks/use-toast"
import { Bell, Check, ExternalLink, X } from "lucide-react"
import React, { useState } from "react"

export interface Notification {
  id: number
  message: string
  type: string
  read: boolean
  created_at: string
  created_by_user?: {
    id: number
    email: string
    name: string
  }
  metadata?: Record<string, any>
}

export function NotificationBell({
  notifications,
  setNotifications,
  loading,
  hasError
}: {
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>
  notifications: Notification[]
  loading: boolean
  hasError: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const { toast } = useToast()
  const [surveyOpen, setSurveyOpen] = useState<{
    open: boolean
    workflowId?: number
  }>({ open: false })
  const [surveyRating, setSurveyRating] = useState<number>(0)
  const [surveyComments, setSurveyComments] = useState<string>("")

  const unreadCount = notifications.filter((n) => !n.read).length

  const markAsRead = async (id: number) => {
    try {
      const response = await fetch("/api/notifications", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, read: true })
      })

      if (!response.ok) {
        console.error(
          `Failed to mark notification as read: ${response.status} ${response.statusText}`
        )
        toast({
          title: "Error",
          description: "Failed to mark notification as read.",
          variant: "destructive"
        })
        return
      }

      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)))
    } catch (error) {
      console.error("Error marking notification as read:", error)
      toast({
        title: "Error",
        description: "Failed to mark notification as read.",
        variant: "destructive"
      })
    }
  }

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter((n) => !n.read)
      console.log("Unread notifications:", unreadNotifications.length)
      if (unreadNotifications.length === 0) return

      const response = await fetch("/api/notifications/mark-all-read", {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      })

      console.log("Mark all as read response:", response.status)
      if (!response.ok) {
        console.error(
          `Failed to mark all notifications as read: ${response.status} ${response.statusText}`
        )

        try {
          const errorData = await response.text()
          console.error("API error response:", errorData)
        } catch {
          console.error("Could not parse error response")
        }

        toast({
          title: "Error",
          description: "Failed to mark all notifications as read.",
          variant: "destructive"
        })
        return
      }

      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
      console.log("All notifications marked as read")
    } catch (error) {
      console.error("Error marking all notifications as read:", error)
      toast({
        title: "Error",
        description: "Failed to mark all notifications as read.",
        variant: "destructive"
      })
    }
  }

  const deleteNotification = async (id: number) => {
    try {
      const response = await fetch(`/api/notifications?id=${id}`, {
        method: "DELETE"
      })

      if (response.ok) {
        setNotifications((prev) => prev.filter((n) => n.id !== id))
        toast({
          title: "Notification deleted",
          description: "The notification has been removed."
        })
      }
    } catch (error) {
      console.error("Error deleting notification:", error)
      toast({
        title: "Error",
        description: "Failed to delete notification.",
        variant: "destructive"
      })
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)

    if (diffInHours < 1) {
      return "Just now"
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (open) {
      markAllAsRead()
    }
  }

  const openSurveyIfNeeded = (n: Notification) => {
    if (n.type === "workflow_completed" && n.metadata?.requires_survey && n.metadata?.workflow_id) {
      setSurveyRating(0)
      setSurveyComments("")
      setSurveyOpen({ open: true, workflowId: n.metadata.workflow_id })
    }
  }

  const submitSurvey = async () => {
    if (!surveyOpen.workflowId) {
      setSurveyOpen({ open: false })
      return
    }
    try {
      const resp = await fetch(`/api/workflows/${surveyOpen.workflowId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // Merge: the API updates step_data wholesale; backend should merge, but we send partial
          step_data: {
            survey: {
              rating: surveyRating,
              comments: surveyComments,
              submitted_at: new Date().toISOString()
            }
          }
        })
      })
      if (!resp.ok) {
        throw new Error("Failed to save survey")
      }
      toast({ title: "Thank you!", description: "Your feedback was recorded." })
      setSurveyOpen({ open: false })
    } catch (e) {
      console.error("Failed to submit survey", e)
      toast({ title: "Error", description: "Could not submit survey.", variant: "destructive" })
    }
  }

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 text-xs flex items-center justify-center"
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
          <div className="p-2 border-b">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Notifications</h3>
              {hasError && (
                <Badge variant="destructive" className="text-xs">
                  Error
                </Badge>
              )}
            </div>
          </div>
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">Loading notifications...</div>
          ) : hasError ? (
            <div className="p-4 text-center">
              <p className="text-sm text-muted-foreground mb-2">Failed to load notifications</p>
              <Button variant="outline" size="sm" onClick={() => fetchNotifications()}>
                Try Again
              </Button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">No notifications</div>
          ) : (
            notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`p-3 cursor-default ${!notification.read ? "bg-muted/50" : ""}`}
                onClick={() => openSurveyIfNeeded(notification)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm ${!notification.read ? "font-medium" : ""}`}>
                      {notification.message}
                    </p>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {notification.metadata?.url && (
                        <a
                          href={notification.metadata.url}
                          className="text-xs text-blue-600 hover:text-blue-800"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      {!notification.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation()
                            markAsRead(notification.id)
                          }}
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteNotification(notification.id)
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-muted-foreground">{notification.type}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(notification.created_at)}
                    </span>
                  </div>
                  {notification.created_by_user && (
                    <p className="text-xs text-muted-foreground mt-1">
                      From:{" "}
                      {notification.created_by_user.name || notification.created_by_user.email}
                    </p>
                  )}
                </div>
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Satisfaction Survey Modal */}
      {surveyOpen.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="font-semibold text-lg mb-1">Rate the workflow result</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Please rate from 0 to 5 stars and share any improvement ideas.
            </p>
            <div className="flex items-center gap-2 mb-4">
              {[0, 1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => setSurveyRating(n)}
                  className={`h-8 w-8 rounded-full border ${surveyRating === n ? "bg-yellow-400 border-yellow-500" : "bg-white"}`}
                  aria-label={`Set rating ${n}`}
                  title={`${n} stars`}
                />
              ))}
            </div>
            <textarea
              className="w-full border rounded-md p-2 text-sm mb-4"
              rows={4}
              placeholder="What could be improved?"
              value={surveyComments}
              onChange={(e) => setSurveyComments(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSurveyOpen({ open: false })}>
                Cancel
              </Button>
              <Button onClick={submitSurvey} disabled={surveyRating < 0 || surveyRating > 5}>
                Submit
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
